import { Box, CircularProgress } from '@mui/material';
import { Upload } from 'lucide-react';
import { useIntl, type IntlShape } from 'react-intl';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import type { DiskImport } from '../hooks';
import {
    footerActions,
    formatImportMessage,
    landedInstanceCount,
    type CommitPhase,
    type CommitReport,
} from '../model';
import { PrimaryButton, QuietButton } from '~/containers/patient-list/shared';

type ImportFooterProps = {
    disk: DiskImport;
    onClose: () => void;
    /**
     * Dismissal is refused while either source has work in flight whose terminal report has not
     * been shown. Shared with the dialog's other dismiss paths, so no close route stays open
     * that the others refuse.
     */
    closeDisabled: boolean;
    /**
     * False while another source owns the pane. Every action here commits or skips the disk queue's
     * current patient, and that patient is off screen then, so the bar keeps only its close button:
     * an Import the reviewer cannot see the subject of is the one press this dialog must not offer.
     */
    showDiskActions: boolean;
};

/** The dialog's action bar: what the wizard did, and what the reviewer may do next. */
export default function ImportFooter({
    disk,
    onClose,
    closeDisabled,
    showDiskActions,
}: ImportFooterProps) {
    const intl = useIntl();
    const { stage, queue, entry, committing, state, actions } = disk;
    const footer = footerActions(
        stage,
        entry,
        committing,
        state.pendingOffer !== undefined
    );
    const primary = showDiskActions ? footer.primary : undefined;
    const report = state.report;

    const commitPhase = stage.kind === 'committing' ? stage.phase : undefined;
    const commitEntryIndex =
        stage.kind === 'committing'
            ? stage.request.entryIndex
            : report?.entryIndex;
    const commitColor =
        report?.kind === 'failed'
            ? 'error.main'
            : report?.kind === 'imported' && report.contradicted
              ? 'warning.main'
              : 'text.secondary';
    const onPrimary =
        primary?.kind === 'restart' ? actions.restart : actions.requestCommit;

    return (
        <Box
            sx={{
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                py: 1,
                px: 2,
                borderTop: 1,
                borderColor: 'divider',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <QuietButton onClick={onClose} disabled={closeDisabled}>
                    {intl.formatMessage({ id: footer.cancelLabelId })}
                </QuietButton>
                {showDiskActions &&
                    queue !== undefined &&
                    commitEntryIndex !== undefined && (
                        <Box
                            role="status"
                            sx={{
                                fontFamily: 'fontFamilyMonospace',
                                fontSize: 11,
                                color: commitColor,
                            }}
                        >
                            <CommitStatus
                                intl={intl}
                                entryIndex={commitEntryIndex}
                                total={queue.entries.length}
                                phase={commitPhase}
                                report={report}
                            />
                        </Box>
                    )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {showDiskActions && footer.showSkip && (
                    <QuietButton onClick={actions.skipEntry}>
                        {intl.formatMessage({
                            id: 'patientList.import.action.skipPatient',
                        })}
                    </QuietButton>
                )}
                {primary === undefined ? null : primary.disabled &&
                  !primary.busy &&
                  primary.hintId ? (
                    <DisabledHint
                        title={intl.formatMessage({ id: primary.hintId })}
                    >
                        <PrimaryButton disabled>
                            {intl.formatMessage({ id: primary.labelId })}
                        </PrimaryButton>
                    </DisabledHint>
                ) : (
                    <PrimaryButton
                        disabled={primary.disabled}
                        onClick={onPrimary}
                    >
                        {primary.busy ? (
                            <CircularProgress
                                size={12}
                                sx={{ color: '#fff' }}
                            />
                        ) : (
                            <Upload size={12} />
                        )}
                        {intl.formatMessage({ id: primary.labelId })}
                    </PrimaryButton>
                )}
            </Box>
        </Box>
    );
}

// Reports what the server did; the report outlives the advance, so the line names its entry.
function CommitStatus({
    intl,
    entryIndex,
    total,
    phase,
    report,
}: {
    intl: IntlShape;
    entryIndex: number;
    total: number;
    phase: CommitPhase | undefined;
    report: CommitReport | undefined;
}) {
    const who = `${intl.formatMessage(
        { id: 'patientList.import.commit.who' },
        { index: entryIndex + 1, total }
    )} · `;

    if (phase !== undefined) {
        return (
            <>
                {who}
                {phase.kind === 'uploading'
                    ? intl.formatMessage(
                          { id: 'patientList.import.commit.uploading' },
                          { count: phase.totalFiles }
                      )
                    : intl.formatMessage({
                          id: 'patientList.import.commit.savingContext',
                      })}
            </>
        );
    }

    if (report === undefined) {
        return null;
    }

    if (report.kind === 'failed') {
        return (
            <>
                {who}
                {formatImportMessage(intl, report.message)}
                {report.landed.length > 0 &&
                    ` ${intl.formatMessage(
                        { id: 'patientList.import.commit.landedNote' },
                        { count: landedInstanceCount(report.landed) }
                    )}`}
            </>
        );
    }

    const { instancesImported, instancesAlreadyPresent, wasNewPatient } =
        report.outcome;
    const parts = [
        intl.formatMessage(
            { id: 'patientList.import.commit.imported' },
            { count: instancesImported }
        ),
        instancesAlreadyPresent > 0
            ? intl.formatMessage(
                  { id: 'patientList.import.commit.alreadyImported' },
                  { count: instancesAlreadyPresent }
              )
            : undefined,
        intl.formatMessage({
            id: wasNewPatient
                ? 'patientList.import.commit.newPatient'
                : 'patientList.import.commit.addedToExisting',
        }),
        // Stated, not only coloured: record check and server disagreed about where these files landed.
        report.contradicted
            ? intl.formatMessage({
                  id: 'patientList.import.commit.contradicted',
              })
            : undefined,
    ].filter(Boolean);
    return (
        <>
            {who}
            {parts.join(' · ')}
        </>
    );
}
